import { Input, OnInit } from '@angular/core';
import { Component } from '@angular/core';

import { AppService } from '../../services/app.service';
import { ParameterService } from '../../services/parameter.service';
import { createFileStub } from '../../shared/file/file.component';
import { VaccinationDocumentService } from '../../services/vaccination-document.service';
import { IVaccinationDocument, IVaccinationDocumentEditModel } from '../../models/VaccinationDocument';
import { Utils } from '../../core/Utils';
import { ClassifierService } from '../../services/classifier.service';
import { ClassifierType, IClassifier } from '../../models/Classifier';
import { IPersonSearchResultItem } from '../../models/Person';

@Component({
    selector: 'app-vaccination-document-edit',
    templateUrl: './edit.component.html'
})
export class VaccinationDocumentEditComponent implements OnInit {
    constructor(
        private app: AppService,
        private service: VaccinationDocumentService,
        private parameters: ParameterService,
        private classifiers: ClassifierService
    ) { }

    @Input() defaultTypeCode?: string;
    @Input() document?: IVaccinationDocument;

    readonly model: IVaccinationDocumentEditModel = <any>{};
    readonly notesMaxLength = 2000;
    readonly getMaxLengthValidationPattern = Utils.getMaxLengthValidationPattern;

    types: IClassifier[] = [];
    attachmentMaxSize: number = 0;
    attachmentExtensions: string = '';
    title: string;
    breadcrumbs: { url: string, text: string }[] = [];
    isNew: boolean;

    studentPickerOpened: boolean;
    canPickStudent: boolean;

    get showPolicyNumber() {
        const type = this.types.find(t => t.Id == this.model.TypeId);
        return type?.Code == 'Insurance';
    }

    get showExpires() {
        const type = this.types.find(t => t.Id == this.model.TypeId);
        return type && type.Code != 'HepatitisB';
    }

    get studentDisplay() {
        return `${this.student?.name} (${this.student?.upn})`;
    }

    private student: { name: string, upn: string };

    ngOnInit() {
        this.canPickStudent = this.app.currentUser.hasRight('VACCINATION_DOCUMENT.ADMIN');

        if (this.document) {
            this.model.PolicyNumber = this.document.PolicyNumber;
            this.model.Expires = this.document.Expires;
            this.model.Notes = this.document.Notes;
            this.model.TypeId = this.document.TypeId;

            if (this.document.FileName) {
                this.model.File = createFileStub(this.document.FileName);
            }

            this.student = {
                name: this.document.OwnerFullName,
                upn: this.document.OwnerUserName
            };
        } else {
            this.isNew = true;

            this.student = {
                name: `${this.app.currentUser.firstName} ${this.app.currentUser.lastName}`,
                upn: this.app.currentUser.username
            };
        }

        this.title = this.isNew
            ? this.app.translate('vaccinationDocument_newTitle')
            : this.document.Type

        this.breadcrumbs = [
            { url: '/vaccination-documents', text: 'vaccinationDocument_listTitle' },
            {
                url: '/vaccination-documents/' + (this.isNew ? 'add' : this.document.Id),
                text: this.isNew ? 'vaccinationDocument_newTitle' : this.document.Type
            }];

        this.app.addLoading(this.parameters.getValues()).subscribe(data => {
            this.attachmentMaxSize = this.parameters.findValue(data, 'VaccinationDocumentFileMaxSize', t => +t, 0);
            this.attachmentExtensions = this.parameters.findValue(data, 'VaccinationDocumentFileExtensions',
                t => '.' + t.split(',').join(',.'), '');
        });

        this.app.addLoading(this.classifiers.get(ClassifierType.VaccinationDocumentType, true)).subscribe(data => {
            this.types = data;

            if (this.defaultTypeCode && this.isNew) {
                this.model.TypeId = data.find(t => t.Code?.toLowerCase() == this.defaultTypeCode.toLowerCase())?.Id;
            }
        });
    }

    pickStudent(student: IPersonSearchResultItem) {
        this.student = {
            name: `${student.FirstName} ${student.LastName}`,
            upn: student.Upn
        };

        this.model.OwnerFullName = this.student.name;
        this.model.OwnerUserName = this.student.upn;

        this.studentPickerOpened = false;
    }

    download() {
        this.app.addLoading(this.service.download(this.document.Id)).subscribe();
    }

    save(isFinal: boolean = false) {
        const proceed = () => {
            const saveModel: IVaccinationDocumentEditModel = {
                ...this.model,
                Expires: this.showExpires ? this.model.Expires : undefined,
                File: this.model.File.size ? this.model.File : undefined,
                IsFinal: isFinal
            };

            const req = this.document
                ? this.service.update(this.document.Id, saveModel)
                : this.service.create(saveModel);

            this.app.addLoading(req).subscribe(id => {
                this.app.notify(isFinal ? 'vaccinationDocument_submitted' : 'vaccinationDocument_saved');
                this.app.navigate(['/vaccination-documents']);
            });
        };

        if (isFinal) {
            this.app.confirm({
                text: this.app.translate('vaccinationDocument_confirmSubmit'),
                okText: this.app.translate('vaccinationDocument_btnSubmit')
            }, ok => {
                if (ok) proceed();
            });
        } else {
            proceed();
        }
    }
}
